import { useState } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';

import Button from '@/design_system/Button';
import Dialog from '@/design_system/Dialog';
import { Footer, FooterButtons, FooterError } from '@/design_system/Footer';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import { useStartSelfHandledTransit } from '@/models/article';
import { useArticleContext } from '@/routes/Requests/contexts/ArticleContext';
import { PrintItemSheetButton } from '@/routes/Requests/Request/components/RequestHeader/PrintOptions';

import { TrackingNumberInput } from '../shared/TrackingNumberInput';

export const ValidateSelfHandledTransitButton = ({
  onActionDone,
}: {
  onActionDone: () => void;
}) => {
  const { article } = useArticleContext();
  const [isModalOpen, setIsModalOpen] = useState(false);

  if (!article.selfHandledTransit?.enabled) {
    return null;
  }

  const label = (
    <Trans id="article.actions.start-self-handled-transit">Confirm package handling</Trans>
  );

  return (
    <>
      <Button variant="primary" size="medium" onPress={() => setIsModalOpen(true)}>
        {label}
      </Button>
      <Dialog
        title={label}
        isOpen={isModalOpen}
        onOpenChange={setIsModalOpen}
        style={{ width: '32rem' }}
      >
        <ValidateSelfHandledTransitModalContent
          setIsOpen={setIsModalOpen}
          onActionDone={onActionDone}
        />
      </Dialog>
    </>
  );
};

const ValidateSelfHandledTransitModalContent = ({
  setIsOpen,
  onActionDone,
}: {
  setIsOpen: (isOpen: boolean) => void;
  onActionDone: () => void;
}) => {
  const { t } = useLingui();
  const { article } = useArticleContext();

  const [trackingNumber, setTrackingNumber] = useState<string | undefined>(undefined);
  const [apiError, setApiError] = useState<string | null>(null);

  const { mutateAsync: startSelfHandledTransit, isPending: isStartSelfHandledTransitPending } =
    useStartSelfHandledTransit({
      articleId: article?.id,
    });

  const confirm = async () => {
    setApiError(null);

    try {
      await startSelfHandledTransit({ trackingNumber });
      setIsOpen(false);
      onActionDone();
    } catch (err: any) {
      setApiError(
        (err?.message as string) ?? t({ id: '_general.error.unknown', message: 'Unknown error' })
      );
    }
  };

  return (
    <>
      <main>
        <Stack gap="1rem">
          <InlineText>
            <Trans id="article.start-self-handled-transit-modal.text">
              By validating, you acknowledge that the delivery or pickup of the item is being
              handled. If the transit has not started yet, please ensure that the item tracking
              sheet is included in the package.
            </Trans>
          </InlineText>
          <TrackingNumberInput
            trackingNumber={trackingNumber}
            setTrackingNumber={setTrackingNumber}
          />
        </Stack>
      </main>
      <Footer>
        {!!apiError && <FooterError>{apiError}</FooterError>}
        <FooterButtons>
          <PrintItemSheetButton article={article} size="medium" variant="secondary" />
          <Button
            variant="primary"
            size="medium"
            isLoading={isStartSelfHandledTransitPending}
            onPress={confirm}
          >
            <Trans id="article.start-self-handled-transit-modal.actions.confirm">Confirm</Trans>
          </Button>
        </FooterButtons>
      </Footer>
    </>
  );
};
